import React, { useCallback } from 'react'
import { t } from 'i18next'
import { Button } from '@pandiafi/design-system'

import { CreatePasswordForm } from './CreatePasswordForm'
import { VerifyPasswordCodeForm } from './VerifyPasswordCodeForm'
import { ResidentAppErrorCode } from '../../../common/types'

import { useAPIConfirmResetPassword } from 'api/apiHooks/useAPIConfirmResetPassword'
import { useAPIConfirmForgotPassword } from 'api/apiHooks/useAPIConfirmForgotPassword'
import { PublicFormResponse } from 'components/PublicFormResponse'

enum ResetPasswordFlowPhases {
  RESET_CODE = 'RESET_CODE',
  GIVE_NEW_PASSWORD = 'GIVE_NEW_PASSWORD',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
}

const resolveConfirmChangePasswordError = (errorMessage: string) => {
  switch (errorMessage) {
    case ResidentAppErrorCode.VALIDATION_ERROR:
    case ResidentAppErrorCode.CODE_MISMATCH_EXCEPTION:
      return t('managePassword:errorCauses.wrongCode')
    case ResidentAppErrorCode.EXPIRED_CODE_EXCEPTION:
      return t('managePassword:errorCauses.expiredCode')
    case ResidentAppErrorCode.INVALID_PASSWORD_EXCEPTION:
      return t('managePassword:errorCauses.wrongPassword')
    case ResidentAppErrorCode.LIMIT_EXCEEDED_EXCEPTION:
    case ResidentAppErrorCode.TOO_MANY_FAILED_ATTEMPTS_EXCEPTION:
    case ResidentAppErrorCode.TOO_MANY_REQUESTS_EXCEPTION:
      return t('managePassword:errorCauses.tooManyAttempts')
    case ResidentAppErrorCode.USER_NOT_CONFIRMED_EXCEPTION:
      return t('managePassword:errorCauses.userNotConfirmed')
    case ResidentAppErrorCode.USER_NOT_FOUND_EXCEPTION:
      return t('managePassword:errorCauses.userNotFound')
    default:
      return null
  }
}

export const ResetPasswordForm: React.FC<{
  /**
   * This function triggers when the password change is completely successful
   */
  onCompletedClick: () => void
  onCancel: () => void
  mode?: 'reset' | 'forgot'
  email?: string | null
}> = ({ onCompletedClick, onCancel, mode = 'reset', email }) => {
  const [flowPhase, setFlowPhase] = React.useState<ResetPasswordFlowPhases>(
    ResetPasswordFlowPhases.RESET_CODE
  )
  const [resetCode, setResetCode] = React.useState<string | null>(null)
  const [errorDetails, setErrorDetails] = React.useState<string | null>(null)

  const { mutateAsync: confirmForgotPassword } = useAPIConfirmForgotPassword()
  const { mutateAsync: confirmResetPassword } = useAPIConfirmResetPassword()

  let contents = null

  const onConfirmResetPassword = useCallback(
    async (newPassword: string) => {
      try {
        if (!resetCode) {
          setFlowPhase(ResetPasswordFlowPhases.ERROR)
          return false
        }

        if (mode === 'reset') {
          await confirmResetPassword({ resetCode, newPassword })
        } else if (email) {
          await confirmForgotPassword({ email, resetCode, newPassword })
        }
        setErrorDetails(null)
        setFlowPhase(ResetPasswordFlowPhases.COMPLETED)

        return true
      } catch (e) {
        if (e instanceof Error) {
          const { message } = e
          if (message) {
            setErrorDetails(resolveConfirmChangePasswordError(message))
          }
        }
        setFlowPhase(ResetPasswordFlowPhases.ERROR)
        return false
      }
    },
    [confirmForgotPassword, confirmResetPassword, email, mode, resetCode]
  )

  switch (flowPhase) {
    case ResetPasswordFlowPhases.RESET_CODE:
      contents = email && (
        <VerifyPasswordCodeForm
          email={email}
          onSubmit={(rc) => {
            setResetCode(rc)
            setErrorDetails(null)
            setFlowPhase(ResetPasswordFlowPhases.GIVE_NEW_PASSWORD)
          }}
        />
      )
      break
    case ResetPasswordFlowPhases.GIVE_NEW_PASSWORD:
      contents = resetCode && (
        <CreatePasswordForm
          customHeader={t('common:userInfo.changePassword')}
          customDescription={t('managePassword:setPasswordText')}
          customSubmitButtonText={t('common:userInfo.changePassword')}
          showCancel={false}
          onSuccess={onConfirmResetPassword}
          onCancel={onCancel}
        />
      )
      break
    case ResetPasswordFlowPhases.COMPLETED:
      contents = (
        <PublicFormResponse
          title={t('managePassword:changedSuccessfully')}
          status="success"
          onClick={() => {
            setResetCode(null)
            setErrorDetails(null)
            onCompletedClick()
          }}
          buttonText={t('common:complete')}
        />
      )
      break
    case ResetPasswordFlowPhases.ERROR:
      contents = (
        <PublicFormResponse
          title={`${t('managePassword:passwordChangeFailedText')}${
            errorDetails ? `: ${errorDetails}` : ''
          }`}
          status="error"
          onClick={() => {
            setResetCode(null)
            setErrorDetails(null)
            onCancel()
          }}
          buttonText={t('common:tryAgain')}
        />
      )
      break
    default:
      break
  }

  return (
    <div className="flex flex-col gap-5 my-4 max-w-64 m-auto">
      {contents}
      {flowPhase !== ResetPasswordFlowPhases.COMPLETED && (
        <Button
          onClick={() => {
            setFlowPhase(ResetPasswordFlowPhases.RESET_CODE)
            setResetCode(null)
            setErrorDetails(null)
            onCancel()
          }}
          variant="text"
        >
          {t('common:cancel')}
        </Button>
      )}
    </div>
  )
}
